// ============================================================
// CineSync Signaling Service
// Manages the WebSocket connection to the Cloudflare Durable Object.
// Handles automatic reconnection with exponential back-off.
// ============================================================

import type { SignalingMessage, SignalingMessageType } from '@/types';

type MessageHandler = (msg: SignalingMessage) => void;

interface SignalingServiceOptions {
  url: string;
  roomId?: string;
  onOpen?: () => void;
  onClose?: (wasClean: boolean) => void;
  onError?: (e: Event) => void;
}

export class SignalingService {
  readonly roomId?: string;
  private ws: WebSocket | null = null;
  private url: string;
  private handlers = new Map<SignalingMessageType, Set<MessageHandler>>();
  private wildcardHandlers = new Set<MessageHandler>();
  private messageQueue: string[] = [];
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 8;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private shouldReconnect = true;
  private onOpen?: () => void;
  private onClose?: (wasClean: boolean) => void;
  private onError?: (e: Event) => void;
  private pingInterval: ReturnType<typeof setInterval> | null = null;

  constructor(opts: SignalingServiceOptions) {
    this.url = opts.url;
    this.roomId = opts.roomId;
    this.onOpen = opts.onOpen;
    this.onClose = opts.onClose;
    this.onError = opts.onError;
  }

  connect(): void {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    try {
      this.ws = new WebSocket(this.url);
    } catch (err) {
      console.warn('[Signaling] Failed to create WebSocket connection:', err);
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.startPing();

      // Flush any messages that were queued before the socket finished connecting
      while (this.messageQueue.length > 0) {
        const queued = this.messageQueue.shift();
        if (queued && this.ws?.readyState === WebSocket.OPEN) {
          try {
            this.ws.send(queued);
          } catch (e) {
            console.warn('[Signaling] Error flushing queued message:', e);
          }
        }
      }

      this.onOpen?.();
    };

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        const msg: SignalingMessage = JSON.parse(event.data as string);
        // Notify type-specific handlers
        this.handlers.get(msg.type)?.forEach((h) => h(msg));
        // Notify wildcard handlers
        this.wildcardHandlers.forEach((h) => h(msg));
      } catch {
        console.warn('[Signaling] Failed to parse message:', event.data);
      }
    };

    this.ws.onclose = (event) => {
      this.stopPing();
      this.onClose?.(event.wasClean);
      if (this.shouldReconnect) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (e) => {
      this.onError?.(e);
    };
  }

  send(msg: Omit<SignalingMessage, 'timestamp'>): void {
    const full: SignalingMessage = { ...msg, timestamp: Date.now() };
    const serialized = JSON.stringify(full);

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(serialized);
    } else {
      // Queue message so critical handshake (e.g. JOIN) is never dropped
      console.log(`[Signaling] Queueing message for delivery upon connection: ${msg.type}`);
      this.messageQueue.push(serialized);
      if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
        this.connect();
      }
    }
  }

  on(type: SignalingMessageType, handler: MessageHandler): () => void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set());
    }
    this.handlers.get(type)!.add(handler);
    return () => this.handlers.get(type)?.delete(handler);
  }

  onAny(handler: MessageHandler): () => void {
    this.wildcardHandlers.add(handler);
    return () => this.wildcardHandlers.delete(handler);
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.stopPing();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close(1000, 'Client disconnecting');
    this.ws = null;
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[Signaling] Max reconnect attempts reached');
      return;
    }
    const delay = Math.min(1000 * 2 ** this.reconnectAttempts, 30_000);
    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(() => {
      console.log(`[Signaling] Reconnecting (attempt ${this.reconnectAttempts})...`);
      this.connect();
    }, delay);
  }

  private startPing(): void {
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send({ type: 'PING', payload: null });
      }
    }, 25_000);
  }

  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }
}

